import {Box, Flex, P} from 'exsportia-components'
import {ChangeEvent, FC, KeyboardEvent, useCallback, useEffect, useState} from 'react'
import styled from 'styled-components'

import {
  getValidateErrorFromResponse,
  isValidPromoCode,
  normalizePromoCode,
  PROMO_CODE_MAX_LENGTH,
} from '../../api/promo-codes/helpers'
import {ApplicableCopy, PromoApplied, PromoError, PromoValidateItem} from '../../api/promo-codes/types'
import {validatePromoCode} from '../../api/promo-codes/validate-promo-code.mutation'
import {theme} from '../../theme/theme'
import {Button} from '../../ui/components/button/button'
import {useLocale} from '../../utils/i18n'
import {SavedCodeChips} from './saved-code-chips'
import {usePromoText} from './use-promo-text'

const LinkButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${theme.colors.primary};
  font-size: ${theme.fontSizes.body};
  text-decoration: underline;
`

const CodeInput = styled.input<{hasError: boolean}>`
  flex: 1;
  min-width: 0;
  height: 48px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.hasError ? theme.colors.error : 'rgba(0, 16, 61, 0.12)')};
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 16px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${theme.colors['text-default']};
  background: ${theme.colors.bg};
  outline: none;

  &:focus {
    border-color: ${(props) => (props.hasError ? theme.colors.error : theme.colors.primary)};
  }

  &:disabled {
    opacity: 0.6;
  }
`

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px 6px 12px;
  border-radius: 24px;
  background: ${theme.colors.green_halftone};
  color: ${theme.colors.success};
`

const ChipCode = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-weight: 600;
`

const RemoveButton = styled.button`
  background: none;
  border: none;
  padding: 0 4px;
  cursor: pointer;
  color: ${theme.colors['text-secondary']};
  font-size: 16px;
  line-height: 1;
`

export type PromoCodeFieldProps = {
  venueId?: string | null
  items?: PromoValidateItem[]
  // The code currently sent to the quote / draft.
  code?: string | null
  // Authoritative result from the quote endpoint.
  applied?: PromoApplied | null
  rejection?: PromoError | null
  isQuoting?: boolean
  currency?: string | null
  // Hidden when the total is already 0 (unless a code is in play).
  hidden?: boolean
  // The customer's saved promotions priced on this quote (bookings); chips use it to pick the best.
  applicable?: ApplicableCopy[]
  onApply: (code: string) => void
  onRemove: () => void
}

const FALLBACK_ERROR: PromoError = {code: 'FALLBACK'}

const TypedPromoField: FC<PromoCodeFieldProps> = ({
  venueId,
  items,
  code,
  applied,
  rejection,
  isQuoting = false,
  currency,
  hidden = false,
  onApply,
  onRemove,
}) => {
  const {l} = useLocale()
  const {errorMessage, discountLabel} = usePromoText(currency)
  const [isOpen, setIsOpen] = useState(Boolean(code || rejection))
  const [input, setInput] = useState(code || '')
  const [localError, setLocalError] = useState<PromoError | null>(null)
  const [showRejection, setShowRejection] = useState(true)
  const [isChecking, setIsChecking] = useState(false)

  useEffect(() => {
    if (code) {
      setInput(code)
      setIsOpen(true)
    }
  }, [code])

  useEffect(() => {
    if (rejection) {
      setShowRejection(true)
      setIsOpen(true)
    }
  }, [rejection])

  const submit = useCallback(async () => {
    const normalized = normalizePromoCode(input)
    setInput(normalized)
    setShowRejection(false)
    if (!isValidPromoCode(normalized)) {
      setLocalError({code: 'PROMO_NOT_FOUND'})
      return
    }
    if (!venueId) {
      return
    }
    setLocalError(null)
    setIsChecking(true)
    try {
      await validatePromoCode({venueId, code: normalized, items})
      onApply(normalized)
    } catch (e) {
      setLocalError(getValidateErrorFromResponse(e) || FALLBACK_ERROR)
    } finally {
      setIsChecking(false)
    }
  }, [input, venueId, items, onApply])

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value.toUpperCase().slice(0, PROMO_CODE_MAX_LENGTH))
    setLocalError(null)
    setShowRejection(false)
  }, [])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        void submit()
      }
    },
    [submit],
  )

  const handleRemove = useCallback(() => {
    setInput('')
    setLocalError(null)
    onRemove()
  }, [onRemove])

  if (hidden && !code) {
    return null
  }

  const isApplied = Boolean(code && applied && applied.code.toUpperCase() === code.toUpperCase())

  if (isApplied && applied) {
    return (
      <Box my='12px'>
        <Chip>
          <span aria-hidden>{'✓'}</span>
          <ChipCode>{applied.code}</ChipCode>
          <span>{discountLabel(applied)}</span>
          <RemoveButton
            type='button'
            aria-label={l('promo_remove_code')}
            onClick={handleRemove}
            disabled={isQuoting}
          >
            {'✕'}
          </RemoveButton>
        </Chip>
      </Box>
    )
  }

  if (!isOpen) {
    return (
      <Box my='12px'>
        <LinkButton type='button' onClick={() => setIsOpen(true)}>
          {l('promo_have_code')}
        </LinkButton>
      </Box>
    )
  }

  const isBusy = isChecking || (Boolean(code) && isQuoting)
  const error = localError || (showRejection ? rejection : null)
  const errorId = 'promo-code-error'

  return (
    <Box my='12px' width='100%'>
      <Flex gap='8px' alignItems='center' width='100%'>
        <CodeInput
          id='promoCode'
          name='promoCode'
          value={input}
          maxLength={PROMO_CODE_MAX_LENGTH}
          autoComplete='off'
          autoCapitalize='characters'
          spellCheck={false}
          placeholder={l('promo_code_placeholder')}
          aria-label={l('promo_code_label')}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          hasError={Boolean(error)}
          disabled={isBusy}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        {/* The secondary button is width 100% by default, which squeezed the input to nothing. */}
        <Box flexShrink={0}>
          <Button
            type='secondary'
            settings={{width: 'max-content'}}
            disabled={isBusy || isQuoting || !input}
            onClick={() => void submit()}
          >
            {isBusy ? l('promo_checking') : l('promo_apply')}
          </Button>
        </Box>
      </Flex>
      {error && (
        <Box mt='6px' id={errorId} role='alert'>
          <P color='error'>{errorMessage(error)}</P>
        </Box>
      )}
    </Box>
  )
}

export const PromoCodeField: FC<PromoCodeFieldProps> = (props) => {
  if (props.hidden && !props.code) {
    return null
  }
  return (
    <>
      <SavedCodeChips
        venueId={props.venueId}
        items={props.items}
        currency={props.currency}
        applicable={props.applicable}
        code={props.code}
        applied={props.applied}
        isQuoting={props.isQuoting}
        onApply={props.onApply}
        onRemove={props.onRemove}
      />
      <TypedPromoField {...props} />
    </>
  )
}
